import clsx from "clsx";
import { formatDate, isSameDay } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { useRestoreContactsMutation } from "../../../../../../features/contactsApi";
import { addBannerWithTimeout } from "../../../../../lib/centralizedBanner/slice";
import { ContactType, FindAllContactsFilters, FindContactsTargetResponse } from "../../../../../lib/filters/dto";
import { keycloak } from "../../../../../lib/keycloak";
import { useFindAllPreferencesQuery, useUpdatePreferencesMutation } from "../../../../../lib/preferences/api";
import { targetListPersonColumns } from "../../../../../lib/preferences/dto";
import { useAppDispatch, useAppSelector } from "../../../../../lib/redux/hooks";
import { colors } from "../../../../../ui/colors";
import { CheckCircleIcon } from "../../../../../ui/icons/checkCircle";
import { CircleIcon } from "../../../../../ui/icons/circle";
import { EditIcon } from "../../../../../ui/icons/edit";
import { LinkedinIcon } from "../../../../../ui/icons/linkedin";
import { MailIcon } from "../../../../../ui/icons/mail";
import { TrashIcon } from "../../../../../ui/icons/trash";
import { UserPlusIcon } from "../../../../../ui/icons/userPlus";
import { Badge } from "../../../../../ui/molecules/badge";
import Button from "../../../../../ui/molecules/button";
import Checkbox from "../../../../../ui/molecules/checkbox";
import { Spinner } from "../../../../../ui/molecules/spinner";
import { Dropdown } from "../../../../../ui/organisms/dropdown";
import { CustomTable } from "../../../../../ui/organisms/table";
import { EmailsMessagesDTO } from "../../../../projects/mails/dto";
import { CustomerAccessEnum } from "../../../../users/dto";
import { ChangeStatusTooltip } from "../../../contacts/changeStatusTooltip";
import { CreateNoteTooltip } from "../../../contacts/createNoteTooltip";
import { Favorite } from "../../../contacts/updateFavorite";
import { ContactStateEnum, NoteDTO } from "../../../dto";

interface Props {
    contacts: FindContactsTargetResponse<ContactType.PERSON>
    selectedIds: { contactId: string, personId: string }[]
    setSelectedIds: (arg: { contactId: string, personId: string }[]) => void
    setShowPopup: (arg: boolean) => void
    filters: FindAllContactsFilters
    setFilters: (arg: Partial<FindAllContactsFilters>) => void
    isLoadingEmailsHistory: boolean
    emailsHistory: { messages: EmailsMessagesDTO[] }
}

export function TargetPeopleTable(props: Props) {
    const targetId = useParams().targetId as string;
    const { selectedIds, setSelectedIds, setShowPopup, setFilters, filters, contacts, isLoadingEmailsHistory, emailsHistory } = props
    const usersState = useAppSelector((state) => state.users);
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    // RTK Query hooks for preferences
    const { data: preferencesResponse } = useFindAllPreferencesQuery(
        keycloak.subject!,
        { skip: !keycloak.subject }
    );
    const [updatePreferencesMutation] = useUpdatePreferencesMutation();
    const [restoreContacts] = useRestoreContactsMutation();

    const handleRestoreContacts = async (ids: string[]) => {
        try {
            await restoreContacts({ contacts: ids, targetList: targetId }).unwrap();
            dispatch(addBannerWithTimeout({
                message: 'Contatti ripristinati con successo.',
                variant: 'success'
            }));
        } catch (error) {
            dispatch(addBannerWithTimeout({
                message: 'Si è verificato un errore durante il ripristino dei contatti.',
                variant: 'error'
            }));
        }
    }

    // Utility function to get column width
    const getColumnWidth = (column: string) => {
        return preferencesResponse?.data[0]?.targetListPersonPreferences?.find(
            col => col.column === column
        )?.width ?? 150;
    };

    function lastNote(notes: NoteDTO[] | null): string {
        if (!notes || notes.length <= 0) {
            return '-'
        } if (notes && notes.length > 0) {
            return notes[notes?.length - 1].note
        }
        else return '-'
    }

    // useEffect(() => {
    //     let id: NodeJS.Timeout
    //     if (contactsState.listIndexToHighlight) {
    //         id = setTimeout(() => {
    //             dispatch(setListIndexToHighlight(undefined))
    //         }, 8000);
    //         return () => clearTimeout(id)
    //     }
    // }, [contactsState.listIndexToHighlight])

    return (
        <CustomTable
            asc={filters.order}
            settedOrder={filters.sort}
            tableHeight={
                keycloak.hasRealmRole('customer') && usersState.findMeResponse?.customerAccess !== CustomerAccessEnum.FULL ?
                    '100%' :
                    filters.outcome.map(v => v.value).includes(ContactStateEnum.TORECALL) ? 'calc(100% - 84px)' : 'calc(100% - 48px)'}
            prevOrderHeads={[
                <th className="text-left w-10">
                    <Checkbox
                        shape={"square"}
                        checked={
                            selectedIds.length ===
                            contacts?.data.length
                        }
                        onCheck={function (value: boolean): void {
                            if (
                                selectedIds.length ===
                                contacts?.data.length
                            ) {
                                setSelectedIds([]);
                            } else {
                                setSelectedIds(
                                    contacts?.data?.map(
                                        (person) => ({ contactId: person.id, personId: person.contact.id })
                                    )!
                                );
                            }
                        }}
                    />
                </th>
            ]}
            postOrderHeads={[
                <th className="text-left">
                    <div className="flex gap-[4px]" />
                </th>
            ]}
            preferences={preferencesResponse?.data[0]?.targetListPersonPreferences?.map(pref => ({ ...pref, order: targetListPersonColumns.find(col => col.column === pref.column)?.order })) ?? []}
            setPreferences={(items) => {
                updatePreferencesMutation({
                    data: {
                        owner: preferencesResponse?.data[0]?.owner!,
                        personPreferences: preferencesResponse?.data[0]?.personPreferences!,
                        companyPreferences: preferencesResponse?.data[0]?.companyPreferences!,
                        targetListPreferences: preferencesResponse?.data[0]?.targetListPreferences!,
                        targetListCompanyPreferences: preferencesResponse?.data[0]?.targetListCompanyPreferences!,
                        targetListPersonPreferences: items,
                        toVerifyBinPersonPreferences: preferencesResponse?.data[0]?.toVerifyBinPersonPreferences!,
                        toVerifyBinCompanyPreferences: preferencesResponse?.data[0]?.toVerifyBinCompanyPreferences!,
                    },
                    id: preferencesResponse?.data[0]?.id!,
                });
            }}
            setOrderFilters={(order) => {
                setFilters({ order: !filters.order, sort: order })
            }}
            rows={contacts?.data.map((d, index) => {
                let emailsToCheck: string[] = []
                emailsToCheck = [...emailsToCheck, ...(d.contact.emails || [])]
                emailsToCheck = [...emailsToCheck, ...(d.sendEmails?.map(email => email) || [])]
                return ({
                    key: d.id,
                    highlight: isSameDay(new Date(d.lastContactDate!), new Date()),
                    // highlightPulse: contactsState.listIndexToHighlight === index,
                    data: [
                        {
                            item: <td className="text-text-sm font-semibold text-neutral-600">
                                <Checkbox
                                    shape={"square"}
                                    checked={selectedIds.some(id => id.contactId === d.id)}
                                    onCheck={function (value: boolean): void {
                                        if (selectedIds.some(id => id.contactId === d.id)) {
                                            setSelectedIds([
                                                ...selectedIds.filter((id) => id.contactId !== d.id),
                                            ]);
                                        } else {
                                            setSelectedIds([...selectedIds, { contactId: d.id, personId: d.contact.id }]);
                                        }
                                    }}
                                />
                            </td>,
                            group: 'preSort',
                            key: 'checkbox'
                        },
                        {
                            item: <td
                                className={d.contact.callsCounter >= 3 ? "blur-[2.5px]" : ""}
                                onClick={() => {
                                    navigate(`${window.location.pathname + '/' + d.contact.id}/form/${d.id}`)
                                }}
                            >
                                <div
                                    className="cursor-pointer crop-text text-text-sm font-semibold text-brandPrimary-600"
                                    style={{ width: getColumnWidth('Nome referente') }}
                                >
                                    {(d.contact.name ?? "-")}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Nome referente'
                        },
                        {
                            item: <td
                                className={d.contact.callsCounter >= 3 ? "blur-[2.5px]" : ""}
                                onClick={() => {
                                    navigate(`${window.location.pathname + '/' + d.contact.id}/form/${d.id}`)
                                }}
                            >
                                <div
                                    className="cursor-pointer crop-text text-text-sm font-semibold text-brandPrimary-600"
                                    style={{ width: getColumnWidth('Cognome referente') }}
                                >
                                    {(d.contact.surname ?? "-")}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Cognome referente'
                        },
                        {
                            item: <td className={d.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                {
                                    d.contact.linkedinProfile ?
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href={d.contact.linkedinProfile ?? undefined}
                                            className="flex items-center"
                                        >
                                            <LinkedinIcon
                                                color={
                                                    d.contact.linkedinProfile
                                                        ? colors.neutral[400]
                                                        : colors.neutral[200]
                                                }
                                                size={20}
                                            />
                                        </a> : <span className="text-text-sm crop-text font-semibold text-neutral-600" style={{ width: getColumnWidth('Linkedin') }}>-</span>
                                }
                            </td>,
                            group: 'sort',
                            key: 'Linkedin'
                        },
                        {
                            item: <td className={d.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div className="text-text-sm crop-text font-semibold text-neutral-600" style={{ width: getColumnWidth('Ruolo') }}>
                                    {d.contact.role ?? "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Ruolo'
                        },
                        {
                            item: <td className={d.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div
                                    className="text-text-sm crop-text text-neutral-600" style={{ width: getColumnWidth('Data di contatto') }}
                                >
                                    {d.lastContactDate ? formatDate(new Date(d.lastContactDate!), 'dd/MM/yyyy HH:mm') : "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Data di contatto'
                        },
                        {
                            item: <td className={d.contact.callsCounter >= 3 ? "blur-[2.5px]" : ""}>
                                <ChangeStatusTooltip
                                    disabled={d.contact.callsCounter >= 3}
                                    contact={d}
                                    noResponseCount={d.noResponseCount}
                                    contactToWork={{
                                        name: d.contact.name,
                                        surname: d.contact.surname,
                                        phone: d.contact.phone ?? "",
                                        privacy: d.contact.privacy ?? false
                                    }}
                                    width={getColumnWidth('Esito')}
                                    contactId={d.id}
                                    outcome={d.outcome}
                                    info={d.outcomeInfo}
                                    contactType={ContactType.PERSON}
                                    listIndex={index} />
                            </td>,
                            group: 'sort',
                            key: 'Esito'
                        },
                        {
                            item: <td className={d.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : "relative group"}>
                                {d.contact.particularities && d.contact.particularities.length > 0 ? (
                                    <div className="text-text-sm font-semibold text-neutral-600 crop-text" style={{ width: getColumnWidth('Particolarità') }}>
                                        {d.contact.particularities ? (d.contact.particularities[0] + (d.contact.particularities.length > 1 ? (', ..., + ' + (d.contact.particularities.length - 1)) : '')) : '-'}
                                    </div>
                                ) : (
                                    "-"
                                )}
                                {d.contact.particularities && d.contact.particularities.length > 0 && <div className="absolute left-0 bottom-full z-50 hidden group-hover:block text-neutral-700 bg-neutral-200 rounded-md p-1 text-text-xs">
                                    {d.contact.particularities.map((item, index) => (
                                        <span key={index}>
                                            {item}
                                            <br />
                                        </span>
                                    ))}
                                </div>}
                            </td>,
                            group: 'sort',
                            key: 'Particolarità'
                        },
                        {
                            item: <td className={d.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <CreateNoteTooltip
                                    targetListId={targetId}
                                    notes={d.notes}
                                    contactId={d.id}
                                    contactType={ContactType.PERSON}
                                    width={getColumnWidth('Note')}
                                    lastNote={lastNote(d.notes)}
                                />
                            </td>,
                            group: 'sort',
                            key: 'Note'
                        },
                        {
                            item: <td >
                                {d.contact.phone ? (
                                    <a
                                        className={d.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}
                                        rel="noreferrer"
                                        target="_blank"
                                        href={"tel:" + d.contact.phone}
                                    >
                                        <div className="text-text-sm font-semibold text-neutral-600 crop-text" style={{ width: getColumnWidth('Smartphone') }}>
                                            {d.contact.phone}
                                        </div>
                                    </a>
                                ) : (
                                    "-"
                                )}
                            </td>,
                            group: 'sort',
                            key: 'Smartphone'
                        },
                        {
                            item: <td className={d.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                {d.contact.emails ? (
                                    <a
                                        rel="noreferrer"
                                        target="_blank"
                                        href={"mailto:" + d.contact.emails}
                                    >
                                        <div className="text-text-sm font-semibold text-neutral-600 crop-text" style={{ width: getColumnWidth('Email') }}>
                                            {d.contact.emails}
                                        </div>
                                    </a>
                                ) : (
                                    "-"
                                )}
                            </td>,
                            group: 'sort',
                            key: 'Email'
                        },
                        {
                            item: <td
                                className={d.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}
                                onClick={() => {
                                    navigate(`${window.location.pathname + '/' + d.contact.id}/company/${d.id}`)
                                }}
                            >
                                <div
                                    className="cursor-pointer crop-text text-text-sm font-semibold text-brandPrimary-600"
                                    style={{ width: getColumnWidth('Azienda') }}
                                >
                                    {d.contact.companyName ?? "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Azienda'
                        },
                        {
                            item: <td className={d.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none" : ""}>
                                <div
                                    className="text-text-sm font-semibold text-neutral-600" style={{ width: getColumnWidth('Reparto') }}
                                >
                                    {d.contact.department ?? "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Reparto'
                        },
                        {
                            item: <td className={d.contact.callsCounter >= 3 ? "blur-[2.5px] pointer-events-none h-full" : "h-full"}>
                                <div className="flex justify-center items-center">
                                    {d.contact.privacy ? (
                                        <CheckCircleIcon color={colors.neutral[400]} size={20} />
                                    ) : (
                                        <CircleIcon color={colors.neutral[400]} size={20} />
                                    )}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Privacy'
                        },
                        {
                            item: <td className="">
                                <div className="flex items-center justify-center gap-2">
                                    <div className="relative">
                                        {
                                            d.notes && d.notes.length > 0 &&
                                            <div className="h-4 min-w-4 rounded-full px-2 bg-brandPrimary-500 absolute left-1/2 text-label-xs flex items-center justify-center text-white" >
                                                {d.notes.length}
                                            </div>
                                        }
                                        <Button
                                            icon={<EditIcon color={""} size={0} />}
                                            size={"md"}
                                            iconPosition={"only"}
                                            disabled={!d.notes || d.notes.length === 0}
                                            variant={"link"}
                                            color={"gray"}
                                            onClick={() => {
                                                navigate(`${window.location.pathname + '/' + d.contact.id}/notes/${d.id}`)
                                            }}
                                        />
                                    </div>
                                    <div className=" relative">
                                        <Button
                                            icon={<MailIcon color={""} size={0} />}
                                            size={"md"}
                                            iconPosition={"only"}
                                            variant={"link"}
                                            color={"gray"}
                                            onClick={() => {
                                                navigate(`${window.location.pathname + '/' + d.contact.id}/emails/${d.id}`)
                                            }}
                                        />
                                        <div
                                            className={clsx("absolute top-0 right-[0%]", {
                                                hidden: emailsHistory?.messages?.flatMap(item => item).filter(item => emailsToCheck?.includes(item.to_email)).length === 0
                                            })}
                                        >
                                            {
                                                isLoadingEmailsHistory ?
                                                    <Spinner size={10} /> :
                                                    <Badge
                                                        color={
                                                            (emailsHistory?.messages?.flatMap(item => item).filter(item => item.opens_count && emailsToCheck?.includes(item.to_email)).length > 0) ? 'green' : "yellow"}
                                                        label={''}
                                                    />
                                            }
                                        </div>
                                    </div>
                                    {!keycloak.hasRealmRole('customer') && <Favorite contactId={d.id} favorite={d.isFavorite} />}
                                    <div className="w-[20px] flex items-center justify-center cursor-pointer">
                                        <Dropdown
                                            items={[
                                                {
                                                    label: "Modifica",
                                                    icon: <EditIcon color={""} size={0} />,
                                                    onClick: () => {
                                                        navigate(`${window.location.pathname + '/' + d.contact.id}/form/${d.id}`)
                                                    },
                                                },
                                                (
                                                    !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
                                                        d.deletedAt !== null ?
                                                        {
                                                            label: "Ripristina",
                                                            icon: <UserPlusIcon color={""} size={0} />,
                                                            onClick: () => {
                                                                handleRestoreContacts([d.id])
                                                            },
                                                        } :
                                                        null
                                                ),
                                                ((keycloak.hasRealmRole('commercial') || keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) ? null : {
                                                    label: "Cancella",
                                                    icon: <TrashIcon color={""} size={0} />,
                                                    onClick: () => {
                                                        setSelectedIds([{ contactId: d.id, personId: d.contact.id }]);
                                                        setShowPopup(true);
                                                    },
                                                }),
                                            ]}
                                        />
                                    </div>
                                </div>
                            </td>,
                            group: 'postSort',
                            key: 'menu'
                        }
                    ]
                })
            }) ?? []}
        />
    )
}